import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PlayCircle, Loader2, CheckCircle2, XCircle, FileText } from 'lucide-react';

interface DueSchedule {
  id: string;
  accountName: string;
  accountNumber: string | null;
  businessProfileName: string;
  contractTitle: string | null;
  applicationName: string | null;
  name: string;
  frequency: string;
  nextBillingDate: string;
  paymentTerms: string;
  subtotal: number;
  enablePaymentReminders: boolean;
}

interface GenerateResult {
  scheduleId: string;
  invoiceId: string | null;
  success: boolean;
}

const frequencyLabel: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual',
  annual: 'Annual',
};

export function GenerateInvoicesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<GenerateResult[] | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['dueSchedules'],
    queryFn: () => api.getDueSchedules(),
  });

  const schedules = (data?.data || []) as DueSchedule[];

  const generateMutation = useMutation({
    mutationFn: (scheduleIds: string[]) => api.generateBatch(scheduleIds),
    onSuccess: (response) => {
      setResults((response.data || []) as GenerateResult[]);
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ['dueSchedules'] });
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  function toggleSelect(id: string) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  function toggleAll() {
    if (selected.size === schedules.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(schedules.map(s => s.id)));
    }
  }

  function handleGenerate() {
    if (selected.size === 0) return;
    generateMutation.mutate(Array.from(selected));
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Generate Invoices</h1>
          <p className="text-muted-foreground">Review and generate invoices from due recurring schedules</p>
        </div>
        <Button
          onClick={handleGenerate}
          disabled={selected.size === 0 || generateMutation.isPending}
        >
          {generateMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <PlayCircle className="h-4 w-4 mr-2" />
          )}
          Generate Selected ({selected.size})
        </Button>
      </div>

      {/* Results */}
      {results && (
        <Card className="border-green-200 bg-green-50 dark:bg-green-950/20">
          <CardHeader>
            <CardTitle className="text-green-700 dark:text-green-400">Generation Results</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {results.map((r) => {
                const schedule = schedules.find(s => s.id === r.scheduleId);
                return (
                  <div key={r.scheduleId} className="flex items-center gap-3">
                    {r.success ? (
                      <CheckCircle2 className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-destructive" />
                    )}
                    <span className="text-sm">
                      {schedule?.name || r.scheduleId}
                      {r.success && r.invoiceId && (
                        <>
                          {' — '}
                          <Button
                            variant="link"
                            className="p-0 h-auto text-sm"
                            onClick={() => navigate(`/billing/invoices/${r.invoiceId}`)}
                          >
                            View Invoice
                          </Button>
                        </>
                      )}
                      {!r.success && <span className="text-destructive ml-2">Failed</span>}
                    </span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Due Schedules Table */}
      {isLoading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : schedules.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No schedules are due for billing</p>
            <p className="text-sm text-muted-foreground mt-1">Active schedules with a billing date on or before today will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="p-3 w-8">
                    <input
                      type="checkbox"
                      checked={selected.size === schedules.length && schedules.length > 0}
                      onChange={toggleAll}
                      className="h-4 w-4"
                    />
                  </th>
                  <th className="text-left p-3 text-sm font-medium">Schedule</th>
                  <th className="text-left p-3 text-sm font-medium">Account</th>
                  <th className="text-left p-3 text-sm font-medium">Business</th>
                  <th className="text-left p-3 text-sm font-medium">Frequency</th>
                  <th className="text-left p-3 text-sm font-medium">Billing Date</th>
                  <th className="text-left p-3 text-sm font-medium">Terms</th>
                  <th className="text-right p-3 text-sm font-medium">Amount</th>
                </tr>
              </thead>
              <tbody>
                {schedules.map((s) => (
                  <tr
                    key={s.id}
                    className={`border-b hover:bg-muted/30 cursor-pointer ${selected.has(s.id) ? 'bg-primary/5' : ''}`}
                    onClick={() => toggleSelect(s.id)}
                  >
                    <td className="p-3">
                      <input
                        type="checkbox"
                        checked={selected.has(s.id)}
                        onChange={() => toggleSelect(s.id)}
                        className="h-4 w-4"
                      />
                    </td>
                    <td className="p-3 text-sm">
                      <div className="font-medium">{s.name}</div>
                      {s.contractTitle && (
                        <div className="text-xs text-muted-foreground">{s.contractTitle}</div>
                      )}
                    </td>
                    <td className="p-3 text-sm">
                      {s.accountName}
                      {s.accountNumber && <span className="text-muted-foreground ml-1">({s.accountNumber})</span>}
                    </td>
                    <td className="p-3 text-sm">{s.businessProfileName}</td>
                    <td className="p-3 text-sm">
                      <Badge variant="secondary">{frequencyLabel[s.frequency] || s.frequency}</Badge>
                    </td>
                    <td className="p-3 text-sm">{formatDate(s.nextBillingDate)}</td>
                    <td className="p-3 text-sm">{s.paymentTerms}</td>
                    <td className="p-3 text-sm text-right font-medium">{formatCurrency(s.subtotal)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
